import { InmateSubscription } from '@reliance/types/models/InmateSubscription';
import { Inmate } from '@reliance/types/models/Inmate';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import api from '../../common/api';
import ConfirmModal from '../../common/dialogs/ConfirmModal';
import { formatCurrency } from '../../format/Currency';
import { useInmateSubscriptionsByInmateId } from '../../hooks/InmateSubscription';
import { useTabLazyLoad } from '../../hooks/useTabLazyLoad';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import BaseHtmlTable from '../../table/BaseHtmlTable';
import { createColumnHelper } from '@tanstack/react-table';
import { useAlerts } from '../../alert/AlertContext';

type InmateSubscriptionsProps = {
  inmate: Inmate;
  tabSelected?: boolean;
  refreshTrigger?: number;
};

const InmateSubscriptions: React.FC<InmateSubscriptionsProps> = (props) => {
  const { inmate, tabSelected, refreshTrigger } = props;
  const alerts = useAlerts();
  const [showCancel, setShowCancel] = useState<InmateSubscription | null>(null);

  // Pass null to prevent auto-execution until the tab is selected
  const subscriptionsResult = useInmateSubscriptionsByInmateId(
    null
  ) as { result?: { data: InmateSubscription[]; total: number }; loading: boolean; execute: (id: number) => void };

  useTabLazyLoad({
    key: inmate.id,
    tabSelected,
    refreshTrigger,
    execute: useCallback(() => {
      if (inmate.id) {
        subscriptionsResult.execute(inmate.id);
      }
    }, [subscriptionsResult, inmate.id])
  });

  useNotifyTabLoading('subscriptions', subscriptionsResult.loading);

  const handleStop = useCallback(async (subscription: InmateSubscription) => {
    try {
      await api.delete(`/inmate-subscription/${subscription.id}`);
      alerts.success('The subscription was successfully stopped');
      setShowCancel(null);
      subscriptionsResult.execute(inmate.id);
    } catch (error) {
      alerts.error('An error occurred while stopping the subscription');
      throw error;
    }
  }, [inmate.id, subscriptionsResult]);

  const columnHelper = createColumnHelper<InmateSubscription>();
  const columns = useMemo(() => [
    columnHelper.accessor((row) => row.subscription.type, {
      id: 'subscription',
      header: 'Subscription',
      enableSorting: true,
    }),
    columnHelper.accessor((row) => {
      return `${formatCurrency(row.subscription.cost)} (Interval: ${row.subscription.interval})`;
    }, {
      id: 'cost',
      header: 'Cost',
      enableSorting: true,
    }),
    columnHelper.accessor((row) => {
      const date = typeof row.dateStarted === 'object' && row.dateStarted.date
        ? moment(row.dateStarted.date)
        : moment(row.dateStarted as any);
      return date.format('YYYY/MM/DD HH:mm:ss');
    }, {
      id: 'dateStarted',
      header: 'Started',
      enableSorting: true,
    }),
    columnHelper.accessor((row) => {
      if (row.dateFinished) {
        const date = typeof row.dateFinished === 'object' && row.dateFinished.date
          ? moment(row.dateFinished.date)
          : moment(row.dateFinished as any);
        return date.format('YYYY/MM/DD HH:mm:ss');
      }
      return '';
    }, {
      id: 'dateFinished',
      header: 'Stopped',
      enableSorting: true,
    }),
    columnHelper.accessor((row) => {
      if (row.dateLastCharged) {
        const date = typeof row.dateLastCharged === 'object' && row.dateLastCharged.date
          ? moment(row.dateLastCharged.date)
          : moment(row.dateLastCharged as any);
        return date.format('YYYY/MM/DD HH:mm:ss');
      }
      return '';
    }, {
      id: 'dateLastCharged',
      header: 'Last Charge',
      enableSorting: true,
    }),
    columnHelper.accessor((row) => {
      if (!row.dateFinished) {
        const date = typeof row.dateNextCharge === 'object' && row.dateNextCharge.date
          ? moment(row.dateNextCharge.date)
          : moment(row.dateNextCharge as any);
        return date.format('YYYY/MM/DD HH:mm:ss');
      }
      return '';
    }, {
      id: 'dateNextCharge',
      header: 'Next Charge',
      enableSorting: true,
    }),
    columnHelper.display({
      id: 'options',
      header: 'Options',
      cell: (info) => {
        const subscription = info.row.original;
        return (
          <>
            {subscription.dateFinished == null && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setShowCancel(subscription)}>
                Cancel Subscription
              </Button>
            )}
            <ConfirmModal
              key={`cancel-subscription-${subscription.id}`}
              show={showCancel === subscription}
              title="Cancel inmate subscription"
              acceptText="Cancel Subscription"
              onAccept={() => handleStop(subscription)}
              onClose={() => setShowCancel(null)}>
              Are you sure you want to cancel the "{subscription.subscription.type}" subscription?
            </ConfirmModal>
          </>
        );
      },
    }),
  ], [columnHelper, showCancel, handleStop]);

  const subscriptions = subscriptionsResult.result?.data || [];

  return (
    <Card>
      <Card.Header>
        <h5>Subscriptions</h5>
      </Card.Header>
      <Card.Body>
        <BaseHtmlTable
          rows={subscriptions}
          columnDefs={columns}
          paginationMode="client"
          perPage={1000}
          loading={subscriptionsResult.loading} />
      </Card.Body>
    </Card>
  );
};

export default InmateSubscriptions;
